<template>
	<!-- Admin MediaLightBox's desktop toolbar actions, shared with the async gallery. Markup, props, icons and test ids
	     are admin's; tracking stays with the caller's event bindings (no v-track here). -->
	<div class="d-flex flex-row justify-center align-center ga-2">
		<div class="d-flex flex-row ga-2">
			<v-tooltip
				v-if="tag"
				location="bottom"
				content-class="lightbox-tooltip"
				:open-on-hover="!touch"
				:open-on-click="false"
				:open-on-focus="false">
				<template #activator="{ props: activatorProps }">
					<v-btn
						elevation="0"
						icon
						variant="text"
						:disabled="tag.disabled"
						v-bind="activatorProps"
						:aria-label="tag.ariaLabel"
						:data-test-id="ids.tag"
						@click="emit('tag')">
						<v-icon>icon-shop-tag-stroke</v-icon>
					</v-btn>
				</template>
				<span>{{ tag.reason || "Manage Tags" }}</span>
			</v-tooltip>

			<v-tooltip
				v-if="rotate"
				location="bottom"
				content-class="lightbox-tooltip"
				:disabled="rotate.inert ? false : touch"
				:open-on-hover="!touch"
				:open-on-click="!!rotate.inert && touch"
				:open-on-focus="!!rotate.inert">
				<template #activator="{ props: activatorProps }">
					<span
						v-bind="activatorProps"
						class="d-inline-flex"
						:tabindex="rotate.inert ? 0 : undefined"
						:role="rotate.inert ? 'button' : undefined"
						:aria-disabled="rotate.inert || undefined"
						:aria-label="rotate.inert ? (rotate.ariaLabel ?? 'Rotate') : undefined"
						:data-test-id="`${ids.rotate}-tooltip`">
						<v-btn
							elevation="0"
							icon
							variant="text"
							:disabled="rotate.disabled"
							:aria-label="rotate.ariaLabel ?? 'Rotate'"
							:aria-hidden="rotate.inert || undefined"
							:data-test-id="ids.rotate"
							@click="emit('rotate')">
							<v-icon>icon-refresh</v-icon>
						</v-btn>
					</span>
				</template>
				<span>{{ rotate.reason || "Rotate" }}</span>
			</v-tooltip>

			<v-tooltip
				v-if="annotate"
				location="bottom"
				content-class="lightbox-tooltip"
				:disabled="annotate.inert ? false : touch"
				:open-on-hover="!touch"
				:open-on-click="!!annotate.inert && touch"
				:open-on-focus="!!annotate.inert">
				<template #activator="{ props: activatorProps }">
					<span
						v-bind="activatorProps"
						class="d-inline-flex"
						:tabindex="annotate.inert ? 0 : undefined"
						:role="annotate.inert ? 'button' : undefined"
						:aria-disabled="annotate.inert || undefined"
						:aria-label="annotate.inert ? (annotate.ariaLabel ?? 'Annotate') : undefined"
						:data-test-id="`${ids.annotate}-tooltip`">
						<v-btn
							elevation="0"
							icon
							variant="text"
							:disabled="annotate.disabled"
							:aria-label="annotate.ariaLabel ?? 'Annotate'"
							:aria-hidden="annotate.inert || undefined"
							:data-test-id="ids.annotate"
							@click="emit('annotate')">
							<v-icon>icon-pencil-edit-stroke</v-icon>
						</v-btn>
					</span>
				</template>
				<span>{{ annotate.reason || "Annotate" }}</span>
			</v-tooltip>

			<slot name="after-annotate" />

			<v-tooltip
				v-if="caption"
				location="bottom"
				content-class="lightbox-tooltip"
				:disabled="caption.inert ? false : touch"
				:open-on-hover="!touch"
				:open-on-click="!!caption.inert && touch"
				:open-on-focus="!!caption.inert">
				<template #activator="{ props: activatorProps }">
					<span
						v-bind="activatorProps"
						class="d-inline-flex"
						:tabindex="caption.inert ? 0 : undefined"
						:role="caption.inert ? 'button' : undefined"
						:aria-disabled="caption.inert || undefined"
						:aria-label="caption.inert ? (caption.ariaLabel ?? 'Add Caption') : undefined"
						:data-test-id="`${ids.caption}-tooltip`">
						<v-btn
							elevation="0"
							icon
							variant="text"
							:disabled="caption.disabled"
							:aria-label="caption.ariaLabel ?? 'Add Caption'"
							:aria-hidden="caption.inert || undefined"
							:data-test-id="ids.caption"
							@click="emit('caption')">
							<div class="icon pt-1">
								<img :src="liveswitchCaption" alt="Processing" />
							</div>
						</v-btn>
					</span>
				</template>
				<span>{{ caption.reason || "Add Caption" }}</span>
			</v-tooltip>

			<slot name="after-caption" />
		</div>
		<div class="d-flex flex-row ga-2">
			<slot name="before-delete" />

			<v-tooltip v-if="remove" location="bottom" content-class="lightbox-tooltip">
				<template #activator="{ props: activatorProps }">
					<v-btn
						elevation="0"
						icon
						variant="text"
						:disabled="remove.disabled"
						v-bind="activatorProps"
						:aria-label="remove.ariaLabel"
						:data-test-id="ids.remove"
						@click="emit('remove')">
						<v-icon color="red">icon-trash-full-stroke</v-icon>
					</v-btn>
				</template>
				<span>{{ remove.reason || "Delete" }}</span>
			</v-tooltip>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { computed } from "vue";
	import liveswitchCaption from "../assets/liveswitch-caption.svg";

	/** One toolbar action. `null` (or omitted) means the action is not rendered. */
	export interface MediaActionState {
		disabled?: boolean;
		/** Tooltip text that explains why the action is unavailable; the action's name is shown otherwise. */
		reason?: string | null;
		/** The action can never apply to this item (GIFs): the tooltip wrapper becomes the focusable explanation. */
		inert?: boolean;
		ariaLabel?: string;
	}
	type MediaAction = "tag" | "rotate" | "annotate" | "caption" | "remove";

	const props = withDefaults(
		defineProps<{
			tag?: MediaActionState | null;
			rotate?: MediaActionState | null;
			annotate?: MediaActionState | null;
			caption?: MediaActionState | null;
			remove?: MediaActionState | null;
			touch?: boolean;
			testIds?: Partial<Record<MediaAction, string>>;
		}>(),
		{ tag: null, rotate: null, annotate: null, caption: null, remove: null, touch: false, testIds: () => ({}) }
	);
	const emit = defineEmits<{ tag: []; rotate: []; annotate: []; caption: []; remove: [] }>();
	const ids = computed<Record<MediaAction, string>>(() => ({
		tag: "photos-lightbox-tag",
		rotate: "photos-lightbox-rotate",
		annotate: "photos-lightbox-annotate",
		caption: "photos-lightbox-caption",
		remove: "photos-lightbox-delete",
		...props.testIds
	}));
</script>

<style>
	/* Admin's tooltip, explicit so the host's Vuetify settings ($border-radius-root) and body font cannot change it.
	   Unscoped: the tooltip content is teleported out of this component. */
	.v-tooltip > .v-overlay__content.lightbox-tooltip {
		border-radius: 4px;
		font-family: Inter;
	}
</style>
